import { createAsyncThunk } from '@reduxjs/toolkit';
import { liveService } from '@/services/live.service';
import { mapApiError } from '@/lib/mapApiError';
import { collectChatHistoryPages } from '@/lib/chatHistoryPagination';
import { shouldSyncLiveKitLayout } from '@/lib/liveLayoutSync';
import { readPublicSessionShared } from '@/lib/publicSessionRead';
import type {
  AnalyticsEventInput,
  LiveKitLayoutInput,
  LiveKitLayoutUpdateResult,
  ChatMessageDto,
  ChatToken,
  GetSessionPresenceParams,
  LivekitParticipant,
  LiveSessionControl,
  LiveSessionResourceStateDto,
  PlaybackInfo,
  PostChatMessageInput,
  PostChatReactionInput,
  PostLiveReactionInput,
  ModeratorToken,
  PresenterToken,
  RoleToken,
  RotatedInviteToken,
  SessionPresenceDto,
  SpeakerToken,
  TriggerEventInput,
  ViewerToken,
  WebinarEventHistoryDto,
  WebinarPresenceStatusDto,
  WebinarPurchaseNotificationConfig,
  WebinarPurchaseNotificationConfigInput,
} from '@/features/live/types';
import type {
  EvaluationSubmissionInput,
  PublicEvergreenChatConfiguration,
  EvaluationTemplate,
  OfferTemplate,
  OfferResults,
  ParticipationStatusDto,
  PublicSurveySubmission,
  PublicWebinarMe,
  PublicWebinarSession,
  QuitPage,
  QuizResults,
  QuizSubmission,
  QuizTemplate,
  SurveyResults,
  SurveyTemplate,
  ThankYouPage,
  WebinarResource,
  WebinarSlide,
} from '@/features/webinar/types';
import type { WorkspaceSocialMediaLink } from '@/features/workspace/types';
import type { AppError } from '@/features/auth/types';
import { AppError as HttpAppError } from '@/http/errors';

export const fetchViewerToken = createAsyncThunk<
  ViewerToken,
  { webinarId: number; sessionIdentifier: string },
  { rejectValue: AppError }
>('live/fetchViewerToken', async ({ webinarId, sessionIdentifier }, { rejectWithValue }) => {
  try {
    return await liveService.getViewerToken(webinarId, sessionIdentifier);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchRoleToken = createAsyncThunk<
  RoleToken,
  { webinarId: number; sessionIdentifier: string; inviteToken: string },
  { rejectValue: AppError }
>(
  'live/fetchRoleToken',
  async ({ webinarId, sessionIdentifier, inviteToken }, { rejectWithValue }) => {
    try {
      return await liveService.getRoleToken(webinarId, sessionIdentifier, inviteToken);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const rotateInviteToken = createAsyncThunk<
  RotatedInviteToken,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    inviteId: number;
  },
  { rejectValue: AppError }
>(
  'live/rotateInviteToken',
  async ({ workspaceId, webinarId, sessionId, inviteId }, { rejectWithValue }) => {
    try {
      return await liveService.rotateInviteToken(workspaceId, webinarId, sessionId, inviteId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchAttendeeInfo = createAsyncThunk<
  PublicWebinarMe,
  { webinarId: number; authorization?: string },
  { rejectValue: AppError }
>('live/fetchAttendeeInfo', async ({ webinarId, authorization }, { rejectWithValue }) => {
  try {
    return await liveService.getAttendeeInfo(webinarId, authorization);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchLivekitParticipants = createAsyncThunk<
  LivekitParticipant[],
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>(
  'live/fetchLivekitParticipants',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      return await liveService.getLivekitParticipants(workspaceId, webinarId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchSessionPresence = createAsyncThunk<
  SessionPresenceDto,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    onlyConverted?: boolean;
    onlyBanned?: boolean;
    orderBy?: string;
    page?: number;
    search?: string;
  },
  { rejectValue: AppError }
>(
  'live/fetchSessionPresence',
  async (
    { workspaceId, webinarId, sessionId, onlyConverted, onlyBanned, orderBy, page, search },
    { rejectWithValue },
  ) => {
    try {
      const params: GetSessionPresenceParams = {};
      if (onlyConverted !== undefined) params.onlyConverted = onlyConverted;
      if (onlyBanned !== undefined) params.onlyBanned = onlyBanned;
      if (orderBy !== undefined) params.orderBy = orderBy;
      if (page !== undefined) params.page = page;
      const trimmedSearch = search?.trim();
      if (trimmedSearch) params.search = trimmedSearch;
      return await liveService.getSessionPresence(workspaceId, webinarId, sessionId, params);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const banPresenceParticipant = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    subscriptionId: number;
  },
  { rejectValue: AppError }
>(
  'live/banPresenceParticipant',
  async ({ workspaceId, webinarId, sessionId, subscriptionId }, { rejectWithValue }) => {
    try {
      return await liveService.banPresenceParticipant(
        workspaceId,
        webinarId,
        sessionId,
        subscriptionId,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const postChatReaction = createAsyncThunk<
  void,
  {
    webinarId: number;
    sessionIdentifier: string;
    authorization: string;
    input: PostChatReactionInput;
  },
  { rejectValue: AppError }
>(
  'live/postChatReaction',
  async ({ webinarId, sessionIdentifier, authorization, input }, { rejectWithValue }) => {
    try {
      return await liveService.postChatReaction(webinarId, sessionIdentifier, authorization, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const postLiveReaction = createAsyncThunk<
  void,
  {
    webinarId: number;
    sessionIdentifier: string;
    authorization: string;
    input: PostLiveReactionInput;
  },
  { rejectValue: AppError }
>(
  'live/postLiveReaction',
  async ({ webinarId, sessionIdentifier, authorization, input }, { rejectWithValue }) => {
    try {
      return await liveService.postLiveReaction(webinarId, sessionIdentifier, authorization, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const postAnalyticsEvent = createAsyncThunk<
  void,
  {
    webinarId: number;
    sessionIdentifier: string;
    authorization: string;
    input: AnalyticsEventInput;
  },
  { rejectValue: AppError }
>(
  'live/postAnalyticsEvent',
  async ({ webinarId, sessionIdentifier, authorization, input }, { rejectWithValue }) => {
    try {
      return await liveService.postAnalyticsEvent(
        webinarId,
        sessionIdentifier,
        authorization,
        input,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const postAttendeeChatMessage = createAsyncThunk<
  ChatMessageDto | null,
  {
    webinarId: number;
    sessionIdentifier: string;
    authorization: string;
    input: PostChatMessageInput;
    commandId?: string;
  },
  { rejectValue: AppError }
>(
  'live/postAttendeeChatMessage',
  async (
    { webinarId, sessionIdentifier, authorization, input, commandId },
    { rejectWithValue },
  ) => {
    try {
      return await liveService.postAttendeeChatMessage(
        webinarId,
        sessionIdentifier,
        authorization,
        input,
        commandId,
      );
    } catch (err) {
      // Le statut distingue un refus (4xx) d'un envoi peut-être accepté (timeout, 5xx).
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
          status: err.status,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
        status: e.status,
      });
    }
  },
);

export const fetchChatHistory = createAsyncThunk<
  ChatMessageDto[],
  {
    webinarId: number;
    sessionIdentifier: string;
    authorization: string;
  },
  { rejectValue: AppError }
>(
  'live/fetchChatHistory',
  async ({ webinarId, sessionIdentifier, authorization }, { rejectWithValue }) => {
    try {
      return await collectChatHistoryPages((page) =>
        liveService.getChatHistory(webinarId, sessionIdentifier, authorization, page),
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const pinChatMessage = createAsyncThunk<
  void,
  {
    webinarId: number;
    sessionIdentifier: string;
    messageId: number;
    authorization: string;
  },
  { rejectValue: AppError }
>(
  'live/pinChatMessage',
  async ({ webinarId, sessionIdentifier, messageId, authorization }, { rejectWithValue }) => {
    try {
      return await liveService.pinChatMessage(
        webinarId,
        sessionIdentifier,
        messageId,
        authorization,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const unpinChatMessage = createAsyncThunk<
  void,
  {
    webinarId: number;
    sessionIdentifier: string;
    messageId: number;
    authorization: string;
  },
  { rejectValue: AppError }
>(
  'live/unpinChatMessage',
  async ({ webinarId, sessionIdentifier, messageId, authorization }, { rejectWithValue }) => {
    try {
      return await liveService.unpinChatMessage(
        webinarId,
        sessionIdentifier,
        messageId,
        authorization,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteChatMessage = createAsyncThunk<
  void,
  {
    webinarId: number;
    sessionIdentifier: string;
    messageId: number;
    authorization: string;
  },
  { rejectValue: AppError }
>(
  'live/deleteChatMessage',
  async ({ webinarId, sessionIdentifier, messageId, authorization }, { rejectWithValue }) => {
    try {
      return await liveService.deleteChatMessage(
        webinarId,
        sessionIdentifier,
        messageId,
        authorization,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const startHlsEgress = createAsyncThunk<
  string,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>('live/startHlsEgress', async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
  try {
    return await liveService.startHlsEgress(workspaceId, webinarId, sessionId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const updateSessionLayout = createAsyncThunk<
  LiveKitLayoutUpdateResult,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    input: LiveKitLayoutInput;
  },
  {
    rejectValue: AppError;
    state: { webinar: { currentWebinarSession?: { id: number; status: string } | null } };
  }
>(
  'live/updateSessionLayout',
  async ({ workspaceId, webinarId, sessionId, input }, { getState, rejectWithValue }) => {
    const camLayoutOnly = input.layout == null && input.activeAsset == null;
    if (
      !shouldSyncLiveKitLayout(getState().webinar.currentWebinarSession, sessionId, camLayoutOnly)
    ) {
      return { applied: false };
    }
    try {
      return await liveService.updateSessionLayout(workspaceId, webinarId, sessionId, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const startCustomRecording = createAsyncThunk<
  LiveSessionControl,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>(
  'live/startCustomRecording',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      return await liveService.startCustomRecording(workspaceId, webinarId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const resetSessionResources = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>(
  'live/resetSessionResources',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      return await liveService.resetSessionResources(workspaceId, webinarId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const startMp4Egress = createAsyncThunk<
  string,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>('live/startMp4Egress', async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
  try {
    return await liveService.startMp4Egress(workspaceId, webinarId, sessionId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const stopEgress = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    egressId: string;
  },
  { rejectValue: AppError }
>(
  'live/stopEgress',
  async ({ workspaceId, webinarId, sessionId, egressId }, { rejectWithValue }) => {
    try {
      return await liveService.stopEgress(workspaceId, webinarId, sessionId, egressId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchWorkspaceChatMessages = createAsyncThunk<
  ChatMessageDto[],
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>(
  'live/fetchWorkspaceChatMessages',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      return await liveService.getWorkspaceChatMessages(workspaceId, webinarId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const postWorkspaceChatMessage = createAsyncThunk<
  ChatMessageDto,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    input: PostChatMessageInput;
  },
  { rejectValue: AppError }
>(
  'live/postWorkspaceChatMessage',
  async ({ workspaceId, webinarId, sessionId, input }, { rejectWithValue }) => {
    try {
      return await liveService.postWorkspaceChatMessage(workspaceId, webinarId, sessionId, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const pinWorkspaceChatMessage = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    messageId: number;
  },
  { rejectValue: AppError }
>(
  'live/pinWorkspaceChatMessage',
  async ({ workspaceId, webinarId, sessionId, messageId }, { rejectWithValue }) => {
    try {
      return await liveService.pinWorkspaceChatMessage(
        workspaceId,
        webinarId,
        sessionId,
        messageId,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const unpinWorkspaceChatMessage = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    messageId: number;
  },
  { rejectValue: AppError }
>(
  'live/unpinWorkspaceChatMessage',
  async ({ workspaceId, webinarId, sessionId, messageId }, { rejectWithValue }) => {
    try {
      return await liveService.unpinWorkspaceChatMessage(
        workspaceId,
        webinarId,
        sessionId,
        messageId,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteWorkspaceChatMessage = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    messageId: number;
  },
  { rejectValue: AppError }
>(
  'live/deleteWorkspaceChatMessage',
  async ({ workspaceId, webinarId, sessionId, messageId }, { rejectWithValue }) => {
    try {
      return await liveService.deleteWorkspaceChatMessage(
        workspaceId,
        webinarId,
        sessionId,
        messageId,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchWorkspaceQuestions = createAsyncThunk<
  { content: ChatMessageDto[]; last: boolean; number: number; totalPages: number },
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    page?: number;
    size?: number;
  },
  { rejectValue: AppError }
>(
  'live/fetchWorkspaceQuestions',
  async ({ workspaceId, webinarId, sessionId, page, size }, { rejectWithValue }) => {
    try {
      return await liveService.getWorkspaceQuestions(workspaceId, webinarId, sessionId, {
        page,
        size,
      });
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const moveWorkspaceMessageToQuestions = createAsyncThunk<
  ChatMessageDto,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    messageId: number;
  },
  { rejectValue: AppError }
>(
  'live/moveWorkspaceMessageToQuestions',
  async ({ workspaceId, webinarId, sessionId, messageId }, { rejectWithValue }) => {
    try {
      return await liveService.moveWorkspaceMessageToQuestions(
        workspaceId,
        webinarId,
        sessionId,
        messageId,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const validateWorkspaceQuestion = createAsyncThunk<
  ChatMessageDto,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    messageId: number;
  },
  { rejectValue: AppError }
>(
  'live/validateWorkspaceQuestion',
  async ({ workspaceId, webinarId, sessionId, messageId }, { rejectWithValue }) => {
    try {
      return await liveService.validateWorkspaceQuestion(
        workspaceId,
        webinarId,
        sessionId,
        messageId,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const triggerEvent = createAsyncThunk<
  void,
  {
    webinarId: number;
    sessionIdentifier: string;
    input: TriggerEventInput;
    authorization?: string;
  },
  { rejectValue: AppError }
>(
  'live/triggerEvent',
  async ({ webinarId, sessionIdentifier, input, authorization }, { rejectWithValue }) => {
    try {
      return await liveService.triggerEvent(webinarId, sessionIdentifier, input, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchAdminChatToken = createAsyncThunk<
  ChatToken,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>(
  'live/fetchAdminChatToken',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      return await liveService.getAdminChatToken(workspaceId, webinarId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchPublicChatToken = createAsyncThunk<
  ChatToken,
  {
    webinarId: number;
    sessionIdentifier: string;
    authorization?: string;
  },
  { rejectValue: AppError }
>(
  'live/fetchPublicChatToken',
  async ({ webinarId, sessionIdentifier, authorization }, { rejectWithValue }) => {
    try {
      return await liveService.getPublicChatToken(webinarId, sessionIdentifier, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const startSession = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>('live/startSession', async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
  try {
    return await liveService.startSession(workspaceId, webinarId, sessionId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchPresenterToken = createAsyncThunk<
  PresenterToken,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>(
  'live/fetchPresenterToken',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      return await liveService.getPresenterToken(workspaceId, webinarId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchModeratorToken = createAsyncThunk<
  ModeratorToken,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>(
  'live/fetchModeratorToken',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      return await liveService.getModeratorToken(workspaceId, webinarId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const closeSession = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>('live/closeSession', async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
  try {
    return await liveService.closeSession(workspaceId, webinarId, sessionId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const muteTrack = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    identity: string;
    trackSid: string;
    muted: boolean;
  },
  { rejectValue: AppError }
>(
  'live/muteTrack',
  async ({ workspaceId, webinarId, sessionId, identity, trackSid, muted }, { rejectWithValue }) => {
    try {
      return await liveService.muteTrack(
        workspaceId,
        webinarId,
        sessionId,
        identity,
        trackSid,
        muted,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchPlayback = createAsyncThunk<
  PlaybackInfo,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>('live/fetchPlayback', async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
  try {
    return await liveService.getPlayback(workspaceId, webinarId, sessionId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchPublicPlayback = createAsyncThunk<
  PlaybackInfo,
  {
    webinarId: number;
    sessionIdentifier: string;
    authorization?: string;
  },
  { rejectValue: AppError }
>(
  'live/fetchPublicPlayback',
  async ({ webinarId, sessionIdentifier, authorization }, { rejectWithValue }) => {
    try {
      return await liveService.getPublicPlayback(webinarId, sessionIdentifier, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchPublicSession = createAsyncThunk<
  PublicWebinarSession,
  {
    webinarId: number;
    sessionIdentifier: string;
  },
  { rejectValue: AppError }
>('live/fetchPublicSession', async ({ webinarId, sessionIdentifier }, { rejectWithValue }) => {
  try {
    return await readPublicSessionShared(webinarId, sessionIdentifier, () =>
      liveService.getPublicSession(webinarId, sessionIdentifier),
    );
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
        status: err.status,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
      status: e.status,
    });
  }
});

export const fetchPublicEvergreen = createAsyncThunk<
  PublicEvergreenChatConfiguration,
  {
    webinarId: number;
    sessionIdentifier: string;
  },
  { rejectValue: AppError }
>('live/fetchPublicEvergreen', async ({ webinarId, sessionIdentifier }, { rejectWithValue }) => {
  try {
    return await liveService.getPublicEvergreen(webinarId, sessionIdentifier);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

// Only the replayed chat mode is read: the stored configuration stays untouched while it reloads.
export const refreshEvergreenChatMode = createAsyncThunk<
  PublicEvergreenChatConfiguration,
  {
    webinarId: number;
    sessionIdentifier: string;
  }
>('live/refreshEvergreenChatMode', ({ webinarId, sessionIdentifier }) =>
  liveService.getPublicEvergreen(webinarId, sessionIdentifier),
);

export const fetchPublicOffer = createAsyncThunk<
  OfferTemplate,
  {
    webinarId: number;
    offerId: number;
    authorization?: string;
  },
  { rejectValue: AppError }
>('live/fetchPublicOffer', async ({ webinarId, offerId, authorization }, { rejectWithValue }) => {
  try {
    return await liveService.getPublicOffer(webinarId, offerId, authorization);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchPublicResourceState = createAsyncThunk<
  LiveSessionResourceStateDto[],
  {
    webinarId: number;
    sessionIdentifier: string;
    authorization?: string;
    // Plusieurs composants réagissent au même event Ably et refetchent l'état des ressources en
    // parallèle. Les appels déclenchés par un event passent `coalesce` pour se fondre dans celui
    // déjà en vol ; les appels dont on exploite le payload (réconciliation) ne le passent pas.
    coalesce?: boolean;
  },
  { rejectValue: AppError; state: { live: { publicResourceStatesLoading: boolean } } }
>(
  'live/fetchPublicResourceState',
  async ({ webinarId, sessionIdentifier, authorization }, { rejectWithValue }) => {
    try {
      return await liveService.getPublicResources(webinarId, sessionIdentifier, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
  {
    condition: ({ coalesce }, { getState }) =>
      !coalesce || !getState().live.publicResourceStatesLoading,
  },
);

export const fetchAdminResourceState = createAsyncThunk<
  LiveSessionResourceStateDto[],
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>(
  'live/fetchAdminResourceState',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      return await liveService.getAdminResourceState(workspaceId, webinarId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const triggerAdminEvent = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    sessionIdentifier: string;
    input: TriggerEventInput;
  },
  { rejectValue: AppError }
>(
  'live/triggerAdminEvent',
  async ({ workspaceId, webinarId, sessionIdentifier, input }, { rejectWithValue }) => {
    try {
      return await liveService.triggerAdminEvent(workspaceId, webinarId, sessionIdentifier, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchSpeakerTokens = createAsyncThunk<
  SpeakerToken[],
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
  },
  { rejectValue: AppError }
>('live/fetchSpeakerTokens', async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
  try {
    return await liveService.getSpeakerTokens(workspaceId, webinarId, sessionId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const removeParticipant = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    identity: string;
  },
  { rejectValue: AppError }
>(
  'live/removeParticipant',
  async ({ workspaceId, webinarId, sessionId, identity }, { rejectWithValue }) => {
    try {
      return await liveService.removeParticipant(workspaceId, webinarId, sessionId, identity);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchPublicSurvey = createAsyncThunk<
  SurveyTemplate,
  {
    webinarId: number;
    surveyId: number;
    authorization: string;
  },
  { rejectValue: AppError }
>('live/fetchPublicSurvey', async ({ webinarId, surveyId, authorization }, { rejectWithValue }) => {
  try {
    return await liveService.getPublicSurvey(webinarId, surveyId, authorization);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchPublicQuiz = createAsyncThunk<
  QuizTemplate,
  {
    webinarId: number;
    quizId: number;
    authorization: string;
  },
  { rejectValue: AppError }
>('live/fetchPublicQuiz', async ({ webinarId, quizId, authorization }, { rejectWithValue }) => {
  try {
    return await liveService.getPublicQuiz(webinarId, quizId, authorization);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchPublicQuitPage = createAsyncThunk<
  QuitPage,
  {
    webinarId: number;
    authorization: string;
  },
  { rejectValue: AppError }
>('live/fetchPublicQuitPage', async ({ webinarId, authorization }, { rejectWithValue }) => {
  try {
    return await liveService.getPublicQuitPage(webinarId, authorization);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const submitPublicQuitPage = createAsyncThunk<
  void,
  {
    webinarId: number;
    authorization: string;
    input: {
      sessionId: number;
      reasonIds: number[];
      otherReasonText?: string | null;
    };
  },
  { rejectValue: AppError }
>('live/submitPublicQuitPage', async ({ webinarId, authorization, input }, { rejectWithValue }) => {
  try {
    return await liveService.submitPublicQuitPage(webinarId, authorization, input);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const submitPublicSurvey = createAsyncThunk<
  PublicSurveySubmission | null,
  {
    webinarId: number;
    templateId: number;
    authorization: string;
    input: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
        freeText: string | null;
      }>;
    };
  },
  { rejectValue: AppError }
>(
  'live/submitPublicSurvey',
  async ({ webinarId, templateId, authorization, input }, { rejectWithValue }) => {
    try {
      return await liveService.submitPublicSurvey(webinarId, templateId, authorization, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const submitPublicSurveyPut = createAsyncThunk<
  PublicSurveySubmission | null,
  {
    webinarId: number;
    templateId: number;
    authorization: string;
    input: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
        freeText: string | null;
      }>;
    };
  },
  { rejectValue: AppError }
>(
  'live/submitPublicSurveyPut',
  async ({ webinarId, templateId, authorization, input }, { rejectWithValue }) => {
    try {
      return await liveService.submitPublicSurveyPut(webinarId, templateId, authorization, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchSurveyResults = createAsyncThunk<
  SurveyResults,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    sessionId?: number;
  },
  { rejectValue: AppError }
>(
  'live/fetchSurveyResults',
  async ({ workspaceId, webinarId, templateId, sessionId }, { rejectWithValue }) => {
    try {
      return await liveService.getSurveyResults(workspaceId, webinarId, templateId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchQuizResults = createAsyncThunk<
  QuizResults,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    sessionId?: number;
  },
  { rejectValue: AppError }
>(
  'live/fetchQuizResults',
  async ({ workspaceId, webinarId, templateId, sessionId }, { rejectWithValue }) => {
    try {
      return await liveService.getQuizResults(workspaceId, webinarId, templateId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchOfferResults = createAsyncThunk<
  OfferResults,
  {
    workspaceId: number;
    webinarId: number;
    offerId: number;
    sessionId?: number;
  },
  { rejectValue: AppError }
>(
  'live/fetchOfferResults',
  async ({ workspaceId, webinarId, offerId, sessionId }, { rejectWithValue }) => {
    try {
      return await liveService.getOfferResults(workspaceId, webinarId, offerId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const submitPublicQuiz = createAsyncThunk<
  QuizSubmission | null,
  {
    webinarId: number;
    templateId: number;
    authorization: string;
    input: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
      }>;
    };
  },
  { rejectValue: AppError }
>(
  'live/submitPublicQuiz',
  async ({ webinarId, templateId, authorization, input }, { rejectWithValue }) => {
    try {
      return await liveService.submitPublicQuiz(webinarId, templateId, authorization, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const submitPublicQuizPut = createAsyncThunk<
  QuizSubmission | null,
  {
    webinarId: number;
    templateId: number;
    authorization: string;
    input: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
      }>;
    };
  },
  { rejectValue: AppError }
>(
  'live/submitPublicQuizPut',
  async ({ webinarId, templateId, authorization, input }, { rejectWithValue }) => {
    try {
      return await liveService.submitPublicQuizPut(webinarId, templateId, authorization, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchMyPublicQuizSubmission = createAsyncThunk<
  QuizSubmission | null,
  {
    webinarId: number;
    templateId: number;
    authorization: string;
  },
  { rejectValue: AppError }
>(
  'live/fetchMyPublicQuizSubmission',
  async ({ webinarId, templateId, authorization }, { rejectWithValue }) => {
    try {
      return await liveService.getMyPublicQuizSubmission(webinarId, templateId, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchPublicSlides = createAsyncThunk<
  {
    content: WebinarSlide[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
  },
  {
    webinarId: number;
    authorization: string;
    params?: { page?: number; size?: number };
  },
  { rejectValue: AppError }
>('live/fetchPublicSlides', async ({ webinarId, authorization, params }, { rejectWithValue }) => {
  try {
    return await liveService.getPublicSlides(webinarId, authorization, params);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchPublicSlide = createAsyncThunk<
  WebinarSlide,
  {
    webinarId: number;
    slideId: number;
    authorization: string;
  },
  { rejectValue: AppError }
>('live/fetchPublicSlide', async ({ webinarId, slideId, authorization }, { rejectWithValue }) => {
  try {
    return await liveService.getPublicSlide(webinarId, slideId, authorization);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchPublicWebinarResources = createAsyncThunk<
  {
    content: WebinarResource[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
  },
  {
    webinarId: number;
    authorization: string;
    params?: { page?: number; size?: number };
  },
  { rejectValue: AppError }
>(
  'live/fetchPublicWebinarResources',
  async ({ webinarId, authorization, params }, { rejectWithValue }) => {
    try {
      return await liveService.getPublicWebinarResources(webinarId, authorization, params);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchPublicResource = createAsyncThunk<
  WebinarResource,
  {
    webinarId: number;
    resourceId: number;
    authorization: string;
  },
  { rejectValue: AppError }
>(
  'live/fetchPublicResource',
  async ({ webinarId, resourceId, authorization }, { rejectWithValue }) => {
    try {
      return await liveService.getPublicResource(webinarId, resourceId, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchPublicWorkspaceSocialMediaLinks = createAsyncThunk<
  WorkspaceSocialMediaLink[],
  {
    workspaceId: number;
  },
  { rejectValue: AppError }
>('live/fetchPublicWorkspaceSocialMediaLinks', async ({ workspaceId }, { rejectWithValue }) => {
  try {
    return await liveService.getPublicWorkspaceSocialMediaLinks(workspaceId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchOfferPurchaseNotificationConfig = createAsyncThunk<
  WebinarPurchaseNotificationConfig,
  { workspaceId: number; webinarId: number; offerId: number },
  { rejectValue: AppError }
>(
  'live/fetchOfferPurchaseNotificationConfig',
  async ({ workspaceId, webinarId, offerId }, { rejectWithValue }) => {
    try {
      const config = await liveService.getOfferPurchaseNotificationConfig(
        workspaceId,
        webinarId,
        offerId,
      );
      return { ...config, webinarId, offerId };
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateOfferPurchaseNotificationConfig = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    offerId: number;
    input: WebinarPurchaseNotificationConfigInput;
  },
  { rejectValue: AppError }
>(
  'live/updateOfferPurchaseNotificationConfig',
  async ({ workspaceId, webinarId, offerId, input }, { rejectWithValue }) => {
    try {
      return await liveService.updateOfferPurchaseNotificationConfig(
        workspaceId,
        webinarId,
        offerId,
        input,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchPublicEvaluation = createAsyncThunk<
  EvaluationTemplate,
  { webinarId: number; authorization: string },
  { rejectValue: AppError }
>('live/fetchPublicEvaluation', async ({ webinarId, authorization }, { rejectWithValue }) => {
  try {
    return await liveService.getPublicEvaluation(webinarId, authorization);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchPublicThankYouPage = createAsyncThunk<
  ThankYouPage,
  { webinarId: number; authorization: string },
  { rejectValue: AppError }
>('live/fetchPublicThankYouPage', async ({ webinarId, authorization }, { rejectWithValue }) => {
  try {
    return await liveService.getPublicThankYouPage(webinarId, authorization);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchPublicParticipationStatuses = createAsyncThunk<
  ParticipationStatusDto[],
  { webinarId: number; authorization: string },
  { rejectValue: AppError }
>(
  'live/fetchPublicParticipationStatuses',
  async ({ webinarId, authorization }, { rejectWithValue }) => {
    try {
      return await liveService.getPublicParticipationStatuses(webinarId, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const submitPublicEvaluation = createAsyncThunk<
  void,
  {
    webinarId: number;
    authorization: string;
    input: EvaluationSubmissionInput;
  },
  { rejectValue: AppError }
>(
  'live/submitPublicEvaluation',
  async ({ webinarId, authorization, input }, { rejectWithValue }) => {
    try {
      return await liveService.submitPublicEvaluation(webinarId, authorization, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchEventHistory = createAsyncThunk<
  WebinarEventHistoryDto,
  {
    webinarId: number;
    sessionIdentifier: string;
    authorization?: string;
  },
  { rejectValue: AppError }
>(
  'live/fetchEventHistory',
  async ({ webinarId, sessionIdentifier, authorization }, { rejectWithValue }) => {
    try {
      return await liveService.getEventHistory(webinarId, sessionIdentifier, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchPresenceStatus = createAsyncThunk<
  WebinarPresenceStatusDto,
  {
    webinarId: number;
    sessionIdentifier: string;
    authorization: string;
  },
  { rejectValue: AppError }
>(
  'live/fetchPresenceStatus',
  async ({ webinarId, sessionIdentifier, authorization }, { rejectWithValue }) => {
    try {
      return await liveService.getPresenceStatus(webinarId, sessionIdentifier, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);
